// Tech leaderboard + commission tracker. Owners see who's making them money
// + how much they owe in commissions. Techs gamify against each other.
import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner, Av, Select, Input } from '../ui.jsx';

export function Performance({ toast }) {
  const [tab, setTab] = useState('leaderboard');
  const [period, setPeriod] = useState('month');
  const [board, setBoard] = useState(null);
  const [comm, setComm] = useState(null);
  const [filter, setFilter] = useState('pending');

  const [portfolioMargin, setPortfolioMargin] = useState(null);
  useEffect(() => { api(`/api/commissions/leaderboard?period=${period}`).then(setBoard).catch(()=>{}); }, [period]);
  useEffect(() => { api(`/api/commissions?status=${filter}`).then(setComm).catch(()=>{}); }, [filter]);
  // Portfolio margin — re-poll every 30s to keep the ticker fresh.
  useEffect(() => {
    const load = () => api('/api/jobs/portfolio-margin').then(setPortfolioMargin).catch(()=>{});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const payOne = async (id) => {
    try { await api(`/api/commissions/${id}/pay`, { method:'POST' }); api(`/api/commissions?status=${filter}`).then(setComm); toast('Marked paid'); }
    catch (e) { toast(e.message, 'error'); }
  };
  const payAll = async () => {
    if (!confirm('Mark ALL pending commissions as paid? (Owner action — usually runs after payroll.)')) return;
    try { const r = await api('/api/commissions/pay-all-pending', { method:'POST', body:{} }); api(`/api/commissions?status=${filter}`).then(setComm); toast(`✓ ${r.marked} marked paid`); }
    catch (e) { toast(e.message, 'error'); }
  };

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Performance</h1>
    </div>

    <div style={{ display:'flex', gap:6, marginBottom:18, background:G.input, padding:4, borderRadius:10, width:'fit-content' }}>
      <Btn sz="sm" v={tab==='leaderboard'?'primary':'ghost'} onClick={()=>setTab('leaderboard')}>🏆 Leaderboard</Btn>
      <Btn sz="sm" v={tab==='commissions'?'primary':'ghost'} onClick={()=>setTab('commissions')}>💵 Commissions</Btn>
      <Btn sz="sm" v={tab==='payplan'?'primary':'ghost'} onClick={()=>setTab('payplan')}>⚙️ Pay Plan</Btn>
      <Btn sz="sm" v={tab==='payroll'?'primary':'ghost'} onClick={()=>setTab('payroll')}>📅 Weekly Payroll</Btn>
      <Btn sz="sm" v={tab==='margin'?'primary':'ghost'} onClick={()=>setTab('margin')}>📊 Live Margin</Btn>
    </div>

    {/* Always-on portfolio margin ticker — visible across all tabs */}
    {portfolioMargin && portfolioMargin.jobCount > 0 && (
      <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor: portfolioMargin.marginPct >= 30 ? G.green+'66' : portfolioMargin.marginPct >= 15 ? G.yellow+'66' : G.red+'66' }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:14, alignItems:'center' }}>
          <div>
            <div style={{ fontSize:10, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>Live Margin</div>
            <div style={{ fontSize:28, fontWeight:800, color: portfolioMargin.marginPct >= 30 ? G.green : portfolioMargin.marginPct >= 15 ? G.yellow : G.red, marginTop:2 }}>
              {portfolioMargin.marginPct}%
            </div>
            <div style={{ fontSize:11, color:G.textMuted }}>{portfolioMargin.jobCount} open jobs</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:G.textMuted, fontWeight:700, textTransform:'uppercase' }}>Revenue</div>
            <div style={{ fontSize:18, fontWeight:700, color:G.text, marginTop:4 }}>{fmtUsd(portfolioMargin.revenueCents/100)}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:G.textMuted, fontWeight:700, textTransform:'uppercase' }}>Labor (burdened)</div>
            <div style={{ fontSize:18, fontWeight:700, color:G.text, marginTop:4 }}>{fmtUsd(portfolioMargin.laborCents/100)}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:G.textMuted, fontWeight:700, textTransform:'uppercase' }}>Materials</div>
            <div style={{ fontSize:18, fontWeight:700, color:G.text, marginTop:4 }}>{fmtUsd(portfolioMargin.materialCents/100)}</div>
          </div>
          <div>
            <div style={{ fontSize:10, color:G.textMuted, fontWeight:700, textTransform:'uppercase' }}>Gross</div>
            <div style={{ fontSize:18, fontWeight:700, color:G.green, marginTop:4 }}>{fmtUsd(portfolioMargin.grossMarginCents/100)}</div>
          </div>
        </div>
      </div>
    )}

    {tab === 'leaderboard' && <div>
      <div style={{ display:'flex', gap:6, marginBottom:14, background:G.input, padding:4, borderRadius:10, width:'fit-content' }}>
        {['week','month','ytd'].map(p => (
          <Btn key={p} sz="sm" v={period===p?'primary':'ghost'} onClick={()=>setPeriod(p)}>
            {p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : 'Year to Date'}
          </Btn>
        ))}
      </div>
      {!board ? <Spinner sz={24}/> : board.leaderboard.length === 0 ? (
        <div style={{ ...styles.card, padding:48, textAlign:'center', color:G.textMuted }}>No completed jobs in this window yet.</div>
      ) : board.leaderboard.map((t, i) => (
        <div key={t.techId} style={{ ...styles.card, padding:16, marginBottom:10, display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:36, textAlign:'center', fontSize:22, fontWeight:800,
            color: i===0 ? '#fbbf24' : i===1 ? '#94a3b8' : i===2 ? '#fb923c' : G.textMuted }}>
            {i===0 ? '🥇' : i===1 ? '🥈' : i===2 ? '🥉' : `#${t.rank}`}
          </div>
          <Av name={t.name}/>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
              <strong style={{ fontSize:15 }}>{t.name}</strong>
              {t.color && <span style={{ width:10, height:10, borderRadius:5, background:t.color }}/>}
              {t.badges.map(b => <Badge key={b} c={G.purple} bg={G.purpleDim}>{b}</Badge>)}
            </div>
            <div style={{ height:6, background:G.input, borderRadius:3, overflow:'hidden', marginTop:6, marginBottom:6 }}>
              <div style={{ width:`${t.revenuePctOfTop}%`, height:'100%', background:`linear-gradient(90deg,${G.purple},${G.cyan})`, transition:'width .4s' }}/>
            </div>
            <div style={{ display:'flex', gap:18, fontSize:12, color:G.textMuted, flexWrap:'wrap' }}>
              <span><strong style={{ color:G.text }}>{t.jobsCompleted}</strong> jobs</span>
              <span><strong style={{ color:G.green }}>{fmtUsd(t.revenue)}</strong> revenue</span>
              <span><strong style={{ color:G.text }}>{fmtUsd(t.avgTicket)}</strong> avg ticket</span>
              {t.onTimePct !== null && <span>⏰ <strong style={{ color:t.onTimePct>=95?G.green:t.onTimePct>=80?G.yellow:G.red }}>{t.onTimePct}%</strong> on time</span>}
              {t.fiveStarReviews > 0 && <span>⭐ <strong style={{ color:G.text }}>{t.fiveStarReviews}</strong>{t.fiveStarReviewsGoogle > 0 && <span style={{ color:G.cyan, marginLeft:4 }}>(🌟 {t.fiveStarReviewsGoogle} Google)</span>}</span>}
              {t.callbacks > 0 && <span>🔁 <strong style={{ color:G.red }}>{t.callbacks}</strong> callbacks</span>}
              {t.hoursSaved > 0 && <span>⚡ <strong style={{ color:G.green }}>{t.hoursSaved}h</strong> saved</span>}
            </div>
            {(t.pay.variablePay > 0 || t.commissionPending > 0) && <div style={{ display:'flex', gap:14, marginTop:8, padding:'8px 12px', background:G.input, borderRadius:6, fontSize:12, flexWrap:'wrap' }}>
              {t.commissionPending > 0 && <span>Comm pending: <strong style={{ color:G.yellow }}>{fmtUsd(t.commissionPending)}</strong></span>}
              {t.pay.efficiencyBonus > 0 && <span>Efficiency: <strong style={{ color:G.green }}>+{fmtUsd(t.pay.efficiencyBonus)}</strong></span>}
              {t.pay.reviewBonus > 0 && <span>Reviews: <strong style={{ color:G.green }}>+{fmtUsd(t.pay.reviewBonus)}</strong></span>}
              {t.pay.upsellBonus > 0 && <span>Upsell: <strong style={{ color:G.green }}>+{fmtUsd(t.pay.upsellBonus)}</strong></span>}
              {t.pay.callbackPenalty > 0 && <span>Callbacks: <strong style={{ color:G.red }}>-{fmtUsd(t.pay.callbackPenalty)}</strong></span>}
              {t.pay.variablePay > 0 && <span style={{ marginLeft:'auto' }}>Bonus take: <strong style={{ color:G.purple }}>{fmtUsd(t.pay.variablePay)}</strong></span>}
            </div>}
          </div>
        </div>
      ))}
    </div>}

    {tab === 'payplan' && <PayPlanTab board={board} toast={toast} onSaved={() => api(`/api/commissions/leaderboard?period=${period}`).then(setBoard)}/>}
    {tab === 'payroll' && <WeeklyPayrollTab toast={toast}/>}

    {tab === 'margin' && <div>
      {!portfolioMargin || portfolioMargin.jobCount === 0 ? (
        <div style={{ ...styles.card, padding:48, textAlign:'center', color:G.textMuted }}>
          No open jobs with revenue + cost data yet. Live margin appears once a job has an estimate / invoice and a tech has clocked time against it.
        </div>
      ) : (
        <div>
          <h3 style={{ margin:'0 0 10px', fontSize:15 }}>⚠ Jobs at risk (margin under 20%)</h3>
          {portfolioMargin.driftWatchlist.length === 0 ? (
            <div style={{ ...styles.card, padding:20, color:G.textMuted, textAlign:'center' }}>All open jobs are healthy. 🎉</div>
          ) : portfolioMargin.driftWatchlist.map(j => (
            <div key={j.jobId} style={{ ...styles.card, padding:14, marginBottom:8, borderLeft:`4px solid ${j.marginPct < 0 ? G.red : G.yellow}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <strong>{j.name}</strong>
                  <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>Revenue {fmtUsd(j.revenueCents/100)}</div>
                </div>
                <div style={{ fontSize:22, fontWeight:800, color: j.marginPct < 0 ? G.red : G.yellow }}>{j.marginPct}%</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>}

    {/* end leaderboard */}
    {tab === 'commissions' && <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', gap:6, background:G.input, padding:4, borderRadius:10 }}>
          {['pending','paid','all'].map(s => (
            <Btn key={s} sz="sm" v={filter===s?'primary':'ghost'} onClick={()=>setFilter(s)}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Btn>
          ))}
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          {comm && <span style={{ fontSize:13, color:G.textMuted }}>
            <strong style={{ color:G.yellow }}>{fmtUsd(comm.summary.pending)}</strong> pending · <strong style={{ color:G.green }}>{fmtUsd(comm.summary.paid)}</strong> paid
          </span>}
          {filter !== 'paid' && <Btn v="primary" onClick={payAll} disabled={!comm?.commissions.filter(c=>c.status==='pending').length}>Mark all paid</Btn>}
        </div>
      </div>
      {!comm ? <Spinner sz={24}/> : comm.commissions.length === 0 ? (
        <div style={{ ...styles.card, padding:48, textAlign:'center', color:G.textMuted }}>
          {filter==='pending' ? 'No pending commissions. Commissions are created automatically when invoices are paid + the assigned tech has a commission rate set.' : 'No records.'}
        </div>
      ) : <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['Tech','Invoice','Base','Rate','Commission','Status','Created',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
          <tbody>{comm.commissions.map(c => (
            <tr key={c.id}>
              <td style={{ ...styles.td, fontWeight:600 }}>{c.techName}</td>
              <td style={styles.td}>{c.invoiceNum || '—'}</td>
              <td style={styles.td}>{fmtUsd(c.baseAmount)}</td>
              <td style={styles.td}>{c.ratePct}%</td>
              <td style={{ ...styles.td, fontWeight:700, color:G.green }}>{fmtUsd(c.amount)}</td>
              <td style={styles.td}><Badge c={c.status==='paid'?G.green:G.yellow} bg={(c.status==='paid'?G.green:G.yellow)+'22'}>{c.status}</Badge></td>
              <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{new Date(c.createdAt).toLocaleDateString()}</td>
              <td style={styles.td}>{c.status === 'pending' && <Btn sz="xs" v="primary" onClick={()=>payOne(c.id)}>Mark Paid</Btn>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>}
    </div>}
  </div>;
}

// ─── Pay Plan editor — set per-tech bonus rates so the leaderboard
// computes everyone's variable pay automatically. Owner-only.
function PayPlanTab({ board, toast, onSaved }) {
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(null);
  if (!board) return <Spinner sz={24}/>;
  if (!board.leaderboard.length) {
    return <div style={{ ...styles.card, padding:48, textAlign:'center', color:G.textMuted }}>Add techs in Team first to set pay plans.</div>;
  }
  const upd = (tid, k, v) => setEdits(e => ({ ...e, [tid]: { ...(e[tid]||{}), [k]: v }}));
  const save = async (t) => {
    const ed = edits[t.techId];
    if (!ed) return;
    setSaving(t.techId);
    try {
      await api(`/api/commissions/techs/${t.techId}/pay`, { method:'PATCH', body: ed });
      toast(`Saved ${t.name}'s pay plan`);
      setEdits(e => { const c={...e}; delete c[t.techId]; return c; });
      onSaved && onSaved();
    } catch(e) { toast(e.message, 'error'); }
    finally { setSaving(null); }
  };
  return <div>
    <div style={{ ...styles.card, padding:14, marginBottom:14, background:G.purpleDim+'33', borderColor:G.purple+'66' }}>
      <strong style={{ color:G.purple, fontSize:13 }}>How performance pay works</strong>
      <div style={{ fontSize:13, color:G.text, marginTop:6, lineHeight:1.5 }}>
        Set a base commission %, then layer bonuses on top:<br/>
        • <strong>Efficiency:</strong> $/hr the tech beats their estimated job time<br/>
        • <strong>5-star review:</strong> flat $ for every 5-star review tied to their job<br/>
        • <strong>Callback penalty:</strong> deducted from variable pool when a job re-opens<br/>
        • <strong>Upsell %:</strong> share of every dollar invoiced above the original estimate
      </div>
    </div>
    {board.leaderboard.map(t => {
      const ed = edits[t.techId] || {};
      const v = (k, d) => ed[k] !== undefined ? ed[k] : d;
      return <div key={t.techId} style={{ ...styles.card, padding:14, marginBottom:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
          <Av name={t.name}/>
          <strong style={{ fontSize:15 }}>{t.name}</strong>
          <span style={{ fontSize:12, color:G.textMuted, marginLeft:'auto' }}>
            This period: {t.jobsCompleted} jobs · {fmtUsd(t.revenue)} revenue
          </span>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:10 }}>
          <Field label="Commission %" hint="of paid invoice"
            value={v('commissionPct', t.pay.commissionPct)}
            onChange={x=>upd(t.techId,'commissionPct',x)}/>
          <Field label="Efficiency $/hr" hint="per hour beat"
            value={v('efficiencyBonusPerHour', t.pay.rates.efficiencyPerHour)}
            onChange={x=>upd(t.techId,'efficiencyBonusPerHour',x)}/>
          <Field label="5-star bonus $" hint="per 5-star"
            value={v('reviewBonus', t.pay.rates.reviewPer5Star)}
            onChange={x=>upd(t.techId,'reviewBonus',x)}/>
          <Field label="Callback penalty $" hint="per re-open"
            value={v('callbackPenalty', t.pay.rates.callbackPenalty)}
            onChange={x=>upd(t.techId,'callbackPenalty',x)}/>
          <Field label="Upsell %" hint="of $ above estimate"
            value={v('upsellBonusPct', t.pay.rates.upsellPct)}
            onChange={x=>upd(t.techId,'upsellBonusPct',x)}/>
        </div>
        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:10 }}>
          <span style={{ fontSize:12, color:G.textMuted, alignSelf:'center' }}>
            Current variable pay: <strong style={{ color:G.purple }}>{fmtUsd(t.pay.variablePay)}</strong>
          </span>
          {edits[t.techId] && <Btn v="primary" sz="sm" onClick={()=>save(t)} disabled={saving===t.techId}>
            {saving===t.techId ? <Spinner sz={12}/> : 'Save'}
          </Btn>}
        </div>
      </div>;
    })}
  </div>;
}

// Weekly payroll calculator. Pulls billable hours × hourly rate + commissions
// earned + jobs closed in the selected week, shows per-tech rows + totals.
// The owner can step backward/forward by week with the arrows.
function WeeklyPayrollTab({ toast }) {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    const dow = d.getDay();
    const offset = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + offset);
    return d.toISOString().slice(0,10);
  });
  const [data, setData] = useState(null);
  const load = (ws) => api(`/api/commissions/weekly-payroll?weekStart=${ws}`).then(setData).catch(e=>toast(e.message,'error'));
  useEffect(() => { load(weekStart); }, [weekStart]);
  const step = (days) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + days);
    setWeekStart(d.toISOString().slice(0,10));
  };
  const fmtDate = (s) => new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' });
  if (!data) return <Spinner sz={28}/>;
  const exportCsv = () => {
    const header = ['Tech','Billable hrs','Total hrs','Hourly $','Base pay','Commission','Gross pay','Jobs done','Revenue closed','Payments collected'];
    const rows = data.rows.map(r => [r.techName, r.billableHours, r.totalHours, r.hourlyRate, r.basePay, r.commission, r.grossPay, r.jobsCompleted, r.revenueClosed, r.paymentsCollected]);
    const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `payroll_${weekStart}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  return <div>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:10 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <Btn sz="sm" onClick={()=>step(-7)}>← Prev week</Btn>
        <div style={{ fontSize:14, fontWeight:700 }}>Week of {fmtDate(data.weekStart)} – {fmtDate(new Date(new Date(data.weekStart).getTime()+6*864e5).toISOString().slice(0,10))}</div>
        <Btn sz="sm" onClick={()=>step(7)}>Next week →</Btn>
      </div>
      <Btn sz="sm" v="cyan" onClick={exportCsv}>📥 Export CSV</Btn>
    </div>

    {/* Totals row at top */}
    <div style={{ ...styles.card, padding:16, marginBottom:14 }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:14 }}>
        <SummaryCell label="Billable hours" value={`${data.totals.billableHours.toFixed(1)}h`}/>
        <SummaryCell label="Gross payroll" value={fmtUsd(data.totals.grossPay)} color={G.green}/>
        <SummaryCell label="Revenue closed" value={fmtUsd(data.totals.revenueClosed)} color={G.cyan}/>
        <SummaryCell label="Payments collected" value={fmtUsd(data.totals.paymentsCollected)} color={G.purple}/>
      </div>
    </div>

    {!data.rows.length ? <div style={{ ...styles.card, padding:40, textAlign:'center', color:G.textMuted }}>No active techs.</div>
      : <div style={{ ...styles.card, padding:0, overflow:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:760 }}>
          <thead><tr>{['Tech','Billable hrs','Hourly','Base pay','Commission','Gross pay','Jobs','Revenue','Collected'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.techId}>
                <td style={{ ...styles.td, fontWeight:600 }}>{r.techName}</td>
                <td style={styles.td}>{r.billableHours.toFixed(1)} <span style={{ color:G.textDim, fontSize:11 }}>/{r.totalHours.toFixed(1)}</span></td>
                <td style={styles.td}>{fmtUsd(r.hourlyRate)}</td>
                <td style={styles.td}>{fmtUsd(r.basePay)}</td>
                <td style={styles.td}>{fmtUsd(r.commission)}</td>
                <td style={{ ...styles.td, fontWeight:700, color:G.green }}>{fmtUsd(r.grossPay)}</td>
                <td style={styles.td}>{r.jobsCompleted}</td>
                <td style={styles.td}>{fmtUsd(r.revenueClosed)}</td>
                <td style={styles.td}>{fmtUsd(r.paymentsCollected)}</td>
              </tr>
            ))}
            <tr style={{ borderTop:`2px solid ${G.cardBorder}` }}>
              <td style={{ ...styles.td, fontWeight:800 }}>TOTALS</td>
              <td style={{ ...styles.td, fontWeight:700 }}>{data.totals.billableHours.toFixed(1)}</td>
              <td style={styles.td}></td>
              <td style={{ ...styles.td, fontWeight:700 }}>{fmtUsd(data.totals.basePay)}</td>
              <td style={{ ...styles.td, fontWeight:700 }}>{fmtUsd(data.totals.commission)}</td>
              <td style={{ ...styles.td, fontWeight:800, color:G.green }}>{fmtUsd(data.totals.grossPay)}</td>
              <td style={{ ...styles.td, fontWeight:700 }}>{data.totals.jobsCompleted}</td>
              <td style={{ ...styles.td, fontWeight:700 }}>{fmtUsd(data.totals.revenueClosed)}</td>
              <td style={{ ...styles.td, fontWeight:700 }}>{fmtUsd(data.totals.paymentsCollected)}</td>
            </tr>
          </tbody>
        </table>
      </div>}
    <div style={{ fontSize:11, color:G.textMuted, marginTop:10, lineHeight:1.5 }}>
      Base pay = billable hours × hourly rate (timesheet kind = "job"). Commission = sum of commission records earned this week. Set hourly rate + commission % per tech in Performance → Pay Plan.
    </div>
  </div>;
}

function SummaryCell({ label, value, color }) {
  return <div>
    <div style={{ fontSize:10, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:22, fontWeight:800, color: color || G.text, marginTop:4 }}>{value}</div>
  </div>;
}

function Field({ label, hint, value, onChange }) {
  return <div>
    <div style={{ fontSize:10, fontWeight:700, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>
      {label}
    </div>
    <Input type="number" value={value ?? 0} step="0.5" min="0" onChange={e=>onChange(Number(e.target.value)||0)}/>
    {hint && <div style={{ fontSize:10, color:G.textDim, marginTop:2 }}>{hint}</div>}
  </div>;
}
